import React from 'react';
import PropTypes from 'prop-types';
import { ModalCardFlow } from '../../_designVariant';
import {
    FooterWidthCard,
    PagesAndLinksCard,
    CustomLinksCard,
    SocialMediaCard,
    ContactInformationCard,
    NewsletterSignupCard,
} from '../cards';

/**
 * Body of the Footer config modal — composes the six footer-related cards.
 * Contract mirrors `NavigationModal`:
 *
 * value shape:
 *   { width:        { maxContentWidth, backgroundFullWidth }
 *     pages:        { about, terms, privacy, disclaimer }
 *     customLinks:  [ { id, url, labels: { nl, en, fr } }, … ]
 *     socialMedia:  [ { id, url }, … ]
 *     contact:      { shopName, tradingName, addressLine, country, phone, email, vatNumber }
 *     newsletter:   { enabled, showInPopup, openPopupAutomatically }
 *   }
 *
 * onChange(section, field, value) — for object sections (width / pages /
 * contact / newsletter), `field` is the property name. For list sections
 * (customLinks / socialMedia), `field` is null and `value` is the new array.
 */
const FooterModal = ({
    value = {},
    onChange,
    onEditPage,
    geocode,
    gmapsApiKey,
    globalFooterWidth,
    onChangeGlobalFooterWidth,
    theme,
    t,
}) => {
    // Object cards: emit (field, val) the same way Navigation does.
    const cardOnChange = (section) => (field, val) => {
        if (onChange) onChange(section, field, val);
    };
    // List cards: parent receives the whole replacement array.
    const listOnChange = (section) => (nextArray) => {
        if (onChange) onChange(section, null, nextArray);
    };

    return (
        // Wide modal → flow the cards into two balanced (masonry) columns on
        // large screens so the canvas is actually used; single column otherwise.
        <ModalCardFlow>
            <FooterWidthCard
                globalFooterWidth={globalFooterWidth}
                onChangeGlobalFooterWidth={onChangeGlobalFooterWidth}
                theme={theme}
                t={t}
            />
            <PagesAndLinksCard
                value={value.pages}
                onChange={cardOnChange('pages')}
                onEditPage={onEditPage}
                theme={theme}
                t={t}
            />
            <CustomLinksCard
                value={value.customLinks}
                onChange={listOnChange('customLinks')}
                theme={theme}
                t={t}
            />
            <SocialMediaCard
                value={value.socialMedia}
                onChange={listOnChange('socialMedia')}
                theme={theme}
                t={t}
            />
            <ContactInformationCard
                value={value.contact}
                onChange={cardOnChange('contact')}
                geocode={geocode}
                gmapsApiKey={gmapsApiKey}
                theme={theme}
                t={t}
            />
            <NewsletterSignupCard
                value={value.newsletter}
                onChange={cardOnChange('newsletter')}
                theme={theme}
                t={t}
            />
        </ModalCardFlow>
    );
};

FooterModal.propTypes = {
    value:             PropTypes.object,
    geocode:           PropTypes.func,
    gmapsApiKey:       PropTypes.string,
    onChange:          PropTypes.func,
    onEditPage:        PropTypes.func,
    globalFooterWidth: PropTypes.shape({
        width:       PropTypes.number,
        bgFullWidth: PropTypes.bool,
    }),
    onChangeGlobalFooterWidth: PropTypes.func,
    theme:             PropTypes.string,
    t:                 PropTypes.func,
};

export default FooterModal;
